// 1. React
import { memo, useEffect, useState, useMemo } from "react";
// 2. Helpers
import { useTranslate } from "helpers";

// 3. Types
import { InvoiceHeaderProps } from "./types";
import { InvoiceOutgoingItemRow } from "services/api/fleet/vehicles/invoices/types";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";

// 4. Form hooks
import {
    useHandleDateChange,
    useHandleDropdownChange,
    useHandleNumberFieldChange,
} from "services/forms/hooks";

// 5. API / Consts
import {
    getPaymentMethodsAPI,
    getTaxTypesAPI,
    getTaxExemptionReasonsAPI,
} from "services/api/finance/lookups/api";
import { getContractRentInvoiceItemsAPI } from "services/api/fleet/vehicles/contracts/api";
import { FleetVehicleContractRentRow } from "services/api/fleet/vehicles/contracts/types";

// 6. UI Components
import FleetContractRentDropdown from "ui/components/Dropdown/FleetContractRentDropdown/FleetContractRentDropdown";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { DatePicker } from "ui/components/DatePicker";
import { NumberField } from "ui/components/TextField/NumberField";


const InvoiceHeader: React.FC<InvoiceHeaderProps> = (props) => {
    const { vehicleId, formValues, setFormValues, errors, setIsLoading, onContractRentsLoaded } = props;

    const { t } = useTranslate();

    const [paymentMethodOptions, setPaymentMethodOptions] = useState<DropdownOption[]>([]);
    const [taxTypeOptions, setTaxTypeOptions] = useState<DropdownOption[]>([]);
    const [allExemptionReasons, setAllExemptionReasons] = useState<any[]>([]);


    useEffect(() => {
        fetchLookups();
    }, []);


    const handleContractSelect = async (contract: FleetVehicleContractRentRow | null) => {
        if (!contract) return;

        if (setIsLoading) setIsLoading(true);

        // Set contract data immediately
        setFormValues((prev) => ({
            ...prev,
            contractRentId: contract.id,
            vehicleId: contract.offerRent?.vehicle?.id ?? prev.vehicleId,
            contactClientId: contract.lesseeContactId ?? contract.offerRent?.contactClient?.id ?? prev.contactClientId,
            dueDate: contract.offerRent?.dueDate ?? prev.dueDate,
            durationDays: contract.offerRent?.durationDays ?? prev.durationDays,
            renterName: contract.lesseeName ?? prev.renterName,
            renterAddress: contract.lesseeAddress ?? prev.renterAddress,
            renterOIB: contract.lesseePersonalIdentificationNumber ?? prev.renterOIB,
        }));

        // Prenose se samo ponavljajuće stavke (najamnina) — pravilo je na poslužitelju.
        // Kazne i jednokratne usluge dodaju se ručno za taj mjesec.
        try {
            const invoiceItems: InvoiceOutgoingItemRow[] = await getContractRentInvoiceItemsAPI(contract.id);
            setFormValues((prev) => ({ ...prev, items: invoiceItems }));
        } catch {
            // greška se prikazuje putem snackbara; stavke ostaju prazne
        }

        if (setIsLoading) setIsLoading(false);
    };

    const fetchLookups = async () => {
        try {
            const [paymentMethods, taxTypes, exemptionReasons] = await Promise.all([
                getPaymentMethodsAPI(),
                getTaxTypesAPI(),
                getTaxExemptionReasonsAPI(),
            ]);

            setPaymentMethodOptions(paymentMethods?.map((item: any) => ({ text: item.name, value: item.id })) ?? []);
            setTaxTypeOptions(taxTypes?.map((item: any) => ({ text: item.name, value: item.id })) ?? []);
            setAllExemptionReasons(exemptionReasons ?? []);
        } catch {
            // error handled by snackbar
        }
    };

    const exemptionReasonOptions = useMemo<DropdownOption[]>(() => {
        if (!formValues.taxTypeId) return [];
        return allExemptionReasons
            .filter((item: any) => item.taxTypeId === formValues.taxTypeId)
            .map((item: any) => ({ text: item.name, value: item.id }));
    }, [allExemptionReasons, formValues.taxTypeId]);

    const handleTaxTypeChange = (_e: any, _name: string, item: DropdownOption) => {
        setFormValues(prev => ({
            ...prev,
            taxTypeId: item.value,
            taxExemptionReasonId: null,
        }));
    };

    const handleDropdownChange = useHandleDropdownChange(
        setFormValues,
        formValues
    );

    const handleDateChange = useHandleDateChange(setFormValues, formValues);

    const handleNumberFieldChange = useHandleNumberFieldChange(
        setFormValues,
        formValues
    );

    return (
        <>
            <div className="flex flex-col gap-3 mb-3">
                <div className="flex gap-4">
                    <div className="flex-1">
                        <FleetContractRentDropdown
                            name="contractRentId"
                            vehicleId={vehicleId}
                            placeholder={t("admin.fleet.vehicle.invoice.add.contract.placeholder")}
                            label={t("admin.fleet.vehicle.invoice.add.contract")}
                            onChange={handleDropdownChange}
                            onContractSelect={handleContractSelect}
                            value={formValues.contractRentId}
                            required
                            showLinkedToInvoice={true}
                            showCancelled={false}
                            autoSelectLast={!formValues.id}
                            disabled={!!formValues.id}
                            onLoaded={onContractRentsLoaded}
                        />
                    </div>
                    <div className="flex-1">
                        <Dropdown
                            name="paymentMethodId"
                            placeholder={t("admin.fleet.vehicle.invoice.add.paymentmethod.placeholder")}
                            label={t("admin.fleet.vehicle.invoice.add.paymentmethod")}
                            options={paymentMethodOptions}
                            onChange={handleDropdownChange}
                            value={formValues.paymentMethodId}
                        />
                    </div>
                </div>

                {formValues.contractRentId && (
                    <>

                        <div className="flex gap-4">
                            <div className="flex-1">
                                <DatePicker
                                    name="deliveryDate"
                                    label={t("admin.fleet.vehicle.invoice.add.deliverydate")}
                                    placeholder={t("admin.fleet.vehicle.invoice.add.deliverydate.placeholder")}
                                    onChange={handleDateChange}
                                    value={formValues.deliveryDate}
                                    width="full"
                                    minDate={new Date()}
                                />
                            </div>
                            <div className="flex-1">
                                <DatePicker
                                    name="dueDate"
                                    label={t("admin.fleet.vehicle.invoice.add.duedate")}
                                    placeholder={t(
                                        "admin.fleet.vehicle.invoice.add.duedate.placeholder"
                                    )}
                                    onChange={handleDateChange}
                                    error={errors.dueDate}
                                    value={formValues.dueDate}
                                    width="full"
                                    required
                                    minDate={new Date()}
                                />
                            </div>
                            <div className="flex flex-1 items-end">
                                <label className="block text-sm mb-2 mr-3 font-medium leading-6 text-[#344054]">
                                    {t("admin.fleet.vehicle.invoice.add.durationdays")}
                                </label>
                                <div className="flex-1">
                                    <NumberField
                                        name="durationDays"
                                        onChange={handleNumberFieldChange}
                                        value={formValues.durationDays}
                                        decimals={0}
                                    ></NumberField>
                                </div>
                            </div>

                        </div>

                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <Dropdown
                                    name="taxTypeId"
                                    placeholder="Odaberite vrstu obračuna"
                                    label="Vrsta obračuna PDV-a"
                                    options={taxTypeOptions}
                                    onChange={handleTaxTypeChange}
                                    value={formValues.taxTypeId}
                                />
                            </div>
                            <div className="col-span-2">
                                {formValues.taxTypeId && (
                                    <Dropdown
                                        name="taxExemptionReasonId"
                                        placeholder="Odaberite razlog oslobođenja"
                                        label="Razlog oslobođenja"
                                        options={exemptionReasonOptions}
                                        onChange={handleDropdownChange}
                                        value={formValues.taxExemptionReasonId}
                                    />
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default memo(InvoiceHeader);
